import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, getSessionCreds } from '../contexts/AuthContext';
import { verifyDevice, humanReason } from '../utils/authFlow';
import { endSessionLog } from '../utils/session';
import { isDemoMobile } from '../utils/demo';

// Keeps a signed-in session honest AFTER login: re-verifies mobile + password +
// this device against the API on every route change (throttled) and whenever
// the app comes back to the foreground. If the account was moved to another
// device (OTP), reset by a Super Admin, deactivated, or the password changed
// elsewhere, this device is signed out immediately with a message on the login
// page. This is what stops the OLD phone once a new one takes the binding.
const MIN_GAP_MS = 60 * 1000;

export default function DeviceGuard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const lastCheck = useRef(0);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!user || isDemoMobile(user.MobileNumber)) return undefined;

    const check = async (force = false) => {
      const now = Date.now();
      if (inFlight.current || (!force && now - lastCheck.current < MIN_GAP_MS)) return;
      const creds = getSessionCreds();
      if (!creds?.mobile) return;
      inFlight.current = true;
      try {
        const res = await verifyDevice(creds.mobile, creds.password);
        lastCheck.current = Date.now();
        // ok+bound → fine. ok+unbound → admin reset this account → sign out so the
        // next sign-in binds explicitly. Any !ok → sign out with the reason.
        const kicked = !res?.ok || res.reason === 'unbound';
        if (kicked) {
          const msg = res?.ok ? humanReason('unbound')
            : res?.reason === 'device_mismatch' ? 'Signed out: this account is now used on another device.'
            : humanReason(res?.reason);
          endSessionLog();
          logout();
          navigate('/', { replace: true, state: { msg } });
        }
      } catch {
        /* offline — keep the session, try again later */
      } finally {
        inFlight.current = false;
      }
    };

    check(true);
    const onVisible = () => { if (document.visibilityState === 'visible') check(true); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.UserId]);

  // Route change → throttled check.
  useEffect(() => {
    if (!user || isDemoMobile(user.MobileNumber)) return;
    if (Date.now() - lastCheck.current < MIN_GAP_MS) return;
    const creds = getSessionCreds();
    if (!creds?.mobile || inFlight.current) return;
    inFlight.current = true;
    verifyDevice(creds.mobile, creds.password).then((res) => {
      lastCheck.current = Date.now();
      if (!res?.ok || res.reason === 'unbound') {
        endSessionLog(); logout();
        navigate('/', { replace: true, state: { msg: res?.ok ? humanReason('unbound') : humanReason(res?.reason) } });
      }
    }).catch(() => {}).finally(() => { inFlight.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
}
